/**
 * Utility functions for handling avatar variants
 */

import { getAccountAvatarPlaceholderVariant } from './accountAvatarStatus';

/**
 * URL zdjęcia profilowego do pokazania w UI — null dla nieaktywnych / zbanowanych lub bez avatara.
 * W komponentach użyj `UserListAvatar` lub `AccountAvatarPlaceholder`.
 *
 * @param {object|null|undefined} user — wiersz `users` z polem `avatar_url`, opcjonalnie `is_active`, `is_banned`
 * @param {number} [size]
 * @returns {string|null}
 */
export function getPublicAvatarUrlForUser(user, size = 100) {
  if (!user || getAccountAvatarPlaceholderVariant(user)) {
    return null;
  }
  return getAvatarUrl(user.avatar_url, size);
}

/**
 * Get avatar URL with specific size variant
 * @param {string} baseUrl - Base avatar URL from database (always points to 200px variant)
 * @param {number} size - Desired size: 100 or 200 (default: 100)
 * @returns {string|null} - Avatar URL for the requested size, or null if baseUrl is not provided
 */
export const getAvatarUrl = (baseUrl, size = 100) => {
  if (!baseUrl) return null;
  
  // Supported sizes
  const validSizes = [100, 200];
  if (!validSizes.includes(size)) {
    console.warn(`Invalid avatar size requested: ${size}. Using default 100px.`);
    size = 100;
  }
  
  // If requesting 200px (default), return base URL as-is
  if (size === 200) {
    return baseUrl;
  }
  
  // Replace _200 with _100 in the URL
  // Pattern: .../avatars/userId/timestamp_200.ext -> .../avatars/userId/timestamp_100.ext
  return baseUrl.replace(/_200\./, `_${size}.`);
};

/**
 * Extract avatar base path from URL for deletion purposes
 * @param {string} avatarUrl - Full avatar URL
 * @returns {string|null} - Base path (userId/timestamp) or null if invalid
 */
export const extractAvatarBasePath = (avatarUrl) => {
  if (!avatarUrl) return null;
  
  // Extract path from URL like: https://...supabase.co/storage/v1/object/public/avatars/userId/timestamp_200.jpg
  const urlParts = avatarUrl.split('/avatars/');
  if (urlParts.length < 2) return null;
  
  const pathPart = urlParts[1];
  // Remove size suffix (_100, _200) and extension to get base path
  const basePath = pathPart.replace(/_(\d+)\.\w+$/, '');
  
  return basePath;
};

/**
 * Get all avatar variant file paths for deletion
 * @param {string} avatarUrl - Full avatar URL
 * @returns {string[]} - Array of file paths for all variants (100, 200)
 */
export const getAllAvatarVariantPaths = (avatarUrl) => {
  const basePath = extractAvatarBasePath(avatarUrl);
  if (!basePath) return [];
  
  // Extract extension from original URL
  const ext = avatarUrl.match(/\.(\w+)$/)?.[1] || 'jpg';
  
  // Return paths for all variants
  return [
    `${basePath}_100.${ext}`,
    `${basePath}_200.${ext}`
  ];
};
